/**
 * driverResource — the Onfleet driver roster, authored against the resource-kit
 * v2 model/view contracts and consumed directly as a `ResourceBundle` by
 * `ResourcePage` (#1805 slice A2). The four record-shaped
 * cell projections moved to `./widgets.tsx`; `fields[].render` has no legacy
 * carrier on the v2 side.
 *
 * `created_at` is declared on the MODEL and left out of the list view, and that
 * asymmetry is the point. The list sorts by `created_at:desc` and always has,
 * but `created_at` was never a column: `ListViewDef.defaultSort.field` is keyed
 * on the model's fields, so the sort needs the field to EXIST while the table
 * must not gain it. Promoting it to a view item would typecheck and keep the
 * sort — and would also add a row to the column-visibility menu, a "Created At"
 * line to the detail card's trailing section (`columnInvisible` answers the
 * LIST surface only; the detail surface reads `invisible.detail`, which has no
 * v2 author), and a diff in
 * `src/__tests__/architecture/resource-field-sets.snapshot.json`.
 *
 * The three filter opt-outs are load-bearing, not tidy. This is a server-paged
 * list capped by `DRIVER_FILTERS`, but two rendered columns show something
 * other than the value a generated filter would match (`is_on_duty` displays
 * the task COUNT, `time_last_seen` displays a formatted datetime over a raw
 * epoch), and `has_tasks` is a filter-only virtual column that reaches the
 * backend through `filterId`. Drop any one and the operator does not lose a
 * filter — they gain one that answers a different question than the column asks.
 */
import {
  createFieldConstructors,
  defineFormView,
  defineListView,
  defineModel,
} from '@resource-kit/model';
import type { ResourceBundle } from '@resource-kit/controllers/react';
import { driverAPI, toListResult, type OnfleetWorker, DRIVER_FILTERS } from '@/services';
import { DRIVER_SORTS } from '@/services/filters.generated';
import { PERMISSIONS } from '@/permissions';
import i18n from '@/i18n';

const f = createFieldConstructors<OnfleetWorker>();

export const driverModel = defineModel<OnfleetWorker>()({
  name: 'driver',
  idField: 'id',
  // What an operator calls a driver. `recName` has no consumer on the detail
  // surface yet — the header title travels on the host, see
  // `packages/resource-kit-controllers/react/bundle.ts` — so this is the value
  // Stage B resolves to.
  recName: 'display_name',
  fields: {
    onfleet_id: f.id({ label: 'driver.field.onfleetId' }),
    name: f.text({ label: 'driver.field.name' }),
    display_name: f.text({
      label: 'driver.field.displayName',
      widget: 'driverDisplayName',
    }),
    phone: f.phone({ label: 'driver.field.phone' }),
    email: f.email({ label: 'driver.field.email' }),
    vehicle: f.json({ label: 'driver.field.vehicle' }),
    active: f.boolean({ label: 'driver.field.active' }),
    tasks: f.array({ label: 'driver.field.tasks', widget: 'driverTaskSummary' }),
    // Boolean duty flag, but the column renders the active-task COUNT as the
    // headline with duty status as a subtitle. `filterable: false` keeps the
    // generic filter generator from offering an operator set for it — without
    // it, the boolean would surface a filter that no longer matches what the
    // column displays.
    is_on_duty: f.boolean({
      label: 'driver.field.isOnDuty',
      filterable: false,
      widget: 'driverDutySummary',
    }),
    // Filter-only synthetic column — `has_tasks` is not a property of the
    // worker row, only a backend filter id (#1405 `virtual` branch).
    has_tasks: f.virtual.boolean({
      label: 'driver.field.hasTasks',
      filterId: 'has_tasks',
    }),
    // Live Onfleet enrichment (ms epoch) served by the read face; the column
    // renders it as a compact datetime. `filterable: false` stops the generic
    // generator from offering a numeric filter over the raw epoch, which would
    // not match the displayed value.
    time_last_seen: f.datetime({
      label: 'driver.field.lastActivity',
      filterable: false,
      widget: 'driverLastActivity',
    }),
    // Model-only: the sort key, never a column. See the file header.
    created_at: f.datetime({ label: 'driver.field.createdAt' }),
  },
});

export const driverListView = defineListView(driverModel, {
  items: [
    { field: 'onfleet_id', columnInvisible: true, invisible: true },
    { field: 'name', columnInvisible: true },
    { field: 'display_name' },
    { field: 'phone' },
    { field: 'email' },
    { field: 'vehicle', columnInvisible: true },
    { field: 'active', columnInvisible: true },
    { field: 'tasks' },
    { field: 'is_on_duty' },
    { field: 'has_tasks', columnInvisible: true },
    { field: 'time_last_seen' },
  ],
  defaultSort: { field: 'created_at', dir: 'desc' },
  // `capabilities.search` is deliberately absent, not `false`: the definition
  // never declared `searchable`, and `resource-searchable-parity.test.ts` holds
  // drivers in the set of routes that do not implement search.
});

/** Read-only roster: the drawer shows the same columns; there is no write path. */
export const driverFormView = defineFormView(driverModel, {
  groupDisplay: 'tab',
  children: [
    // #1922: `vehicle` used to be a fields group of its own. Since #1918 declared
    // the `'tab'` group display, a group is a rail trigger, and one
    // trigger for one field is pure noise — the vehicle is an attribute of the
    // driver record, so it joins `main` at the tail. `tasks` below keeps its
    // own tab: it carries two fields and 司机任务 is its own CONTEXT.md term,
    // not a property of the driver.
    {
      kind: 'fields',
      id: 'main',
      label: 'driver.group.main',
      items: [
        { field: 'display_name' },
        { field: 'name' },
        { field: 'phone' },
        { field: 'email' },
        { field: 'is_on_duty' },
        { field: 'active' },
        { field: 'vehicle' },
      ],
    },
    {
      kind: 'fields',
      id: 'tasks',
      label: 'driver.group.tasks',
      items: [{ field: 'tasks' }, { field: 'has_tasks' }],
    },
    {
      kind: 'fields',
      id: 'meta',
      label: 'driver.group.meta',
      items: [{ field: 'time_last_seen' }, { field: 'onfleet_id' }],
    },
  ],
});

export const driverResource: ResourceBundle<OnfleetWorker, typeof driverModel.fields> = {
  model: driverModel,
  list: driverListView,
  form: driverFormView,
  host: {
    api: {
      // Only `list` is adapted HERE rather than in the service: `driverAPI.list`
      // has two callers and only this one needs the controllers' `ListResult`.
      // The other — `jobResource`'s driver filter-options loader
      // (`features/dispatch/jobResource.tsx`) — reads just `page.items` off the
      // wire `ListPage`, so converting inside the service would push a type it
      // does not use onto it. `get` needs no adapter: `driverAPI.detail`
      // already returns the bare record (#2167 PR-2).
      list: async (params) => toListResult(await driverAPI.list(params)),
      get: (id) => driverAPI.detail(id),
    },
    label: 'driver.label',
    slug: 'drivers',
    listTitle: 'driver.listTitle',
    listDescription: 'driver.listDescription',
    // drivers re-gated onto `dispatch:read` — the backend folded the
    // legacy `drivers:*` keys into the delivery `dispatch:*` module (#943).
    permission: { list: PERMISSIONS.DISPATCH_READ, read: PERMISSIONS.DISPATCH_READ },
    // Backend filterable whitelist (#contains-filter): name (contains/eq).
    filterable: DRIVER_FILTERS,
    sortable: DRIVER_SORTS,
    searchPlaceholder: 'driver.searchPlaceholder',
    staleTime: 5 * 60 * 1000,
    detail: {
      title: (d) => d.display_name || d.name,
      subtitle: (d) => i18n.t('driver.detail.subtitle', { phone: d.phone || 'N/A' }),
    },
  },
};
